import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSearchParam } from "../../hooks/useSearchParam";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../../redux";
import { dismissTestNotification } from "../../redux/reducers/testNotificationsSlice";
import { DropdownField, EmptyState, Text, View, Spinner } from "../../components/base";
import { EmptyMessage } from "../../components/EmptyMessage";
import { AgentTestAnalytics } from "../../components/AgentTestAnalytics";
import { TestNotifications } from "../../components/TestNotifications";

export const AgentTestingPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const testParam = useSearchParam("test");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTest, setSelectedTest] = useState<string>(testParam);
  const prevSelectedTestRef = useRef<string>(testParam);
  const dispatch = useAppDispatch();
  const testNotifications = useAppSelector((state) => (state.testNotifications as any).notifications[agentId || ""] || []);

  const testListRequest = useApiRequest<{ data: { testNames: string[] } }>();
  const reportRequest = useApiRequest<{ data: any }>();

  useEffect(() => {
    if (agentId) {
      testListRequest.fetch(`/v1/agent-test-analytics/tests?agentId=${agentId}`);
    }
  }, [agentId]);

  useEffect(() => {
    // If there are tests and no selectedTest, set selectedTest to the first one
    const testNames = testListRequest.data?.data?.testNames || [];
    if (testNames.length > 0 && !selectedTest) {
      setSelectedTest(testNames[0]);
    }
  }, [testListRequest.data, selectedTest]);

  // Update URL when selectedTest changes (but not on initial load)
  useEffect(() => {
    if (prevSelectedTestRef.current !== selectedTest) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (selectedTest) {
        newSearchParams.set("test", selectedTest);
      } else {
        newSearchParams.delete("test");
      }
      setSearchParams(newSearchParams);
      prevSelectedTestRef.current = selectedTest;
    }
  }, [selectedTest, searchParams, setSearchParams]);

  // Dismiss notification when test is selected from dropdown
  useEffect(() => {
    if (selectedTest && testNotifications.includes(selectedTest)) {
      dispatch(dismissTestNotification({ agentId: agentId!, testName: selectedTest }));
    }
  }, [selectedTest, testNotifications, dispatch, agentId]);

  useEffect(() => {
    if (selectedTest && agentId) {
      reportRequest.fetch(`/v1/agent-test-analytics/report?agentId=${agentId}&testName=${encodeURIComponent(selectedTest)}`);
    }
  }, [selectedTest, agentId]);

  const handleTestSelect = (testName: string) => {
    setSelectedTest(testName);
  };

  const testNames = testListRequest.data?.data?.testNames || [];

  return (
    <View className="relative flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900">
      <View className="flex flex-col flex-1 overflow-y-auto">
        <View className="w-full max-w-7xl mx-auto">
          {/* Test Notifications */}
          {agentId && (
            <TestNotifications
              agentId={agentId}
              onTestSelect={handleTestSelect}
            />
          )}

          {/* Test Selection */}
          <View className="p-6 flex flex-col gap-4">
            {testListRequest.isLoading ? (
              <View className="flex justify-center py-8">
                <Spinner
                  size={32}
                  variant="brand"
                />
              </View>
            ) : testListRequest.error ? (
              <EmptyMessage
                title="Error loading tests"
                description={String(testListRequest.error)}
                iconName="info"
              />
            ) : testNames.length === 0 ? (
              <EmptyState
                icon="testing"
                title="No Tests Found"
                description="This agent doesn't have any tests yet.&nbsp;&nbsp;Add tests and outcomes to your agent function to start testing."
                actionLabel="View Documentation"
                actionLabelIcon="snippet"
                onAction={() => {
                  window.open("https://voicerun.com/docs", "_blank");
                }}
              />
            ) : (
              <>
                <Text className="text-lg font-semibold">Select Test</Text>
                <DropdownField
                  value={selectedTest}
                  onChange={setSelectedTest}
                  options={testNames.map((name: string) => ({ value: name, label: name }))}
                  className="w-80"
                  placeholder="Select a test"
                />
              </>
            )}
          </View>

          {/* Analytics Dashboard or Empty State */}
          {selectedTest && !testListRequest.isLoading && !testListRequest.error ? (
            <>
              {reportRequest.isLoading ? (
                <View className="flex justify-center py-12">
                  <Spinner
                    size={40}
                    variant="brand"
                  />
                </View>
              ) : reportRequest.error ? (
                <EmptyMessage
                  title="Error loading analytics"
                  description={String(reportRequest.error)}
                  iconName="info"
                />
              ) : reportRequest.data?.data ? (
                <AgentTestAnalytics testData={reportRequest.data.data} />
              ) : (
                <EmptyMessage
                  title="No Analytics"
                  description="Select a test to view analytics."
                  iconName="bar-chart-2"
                />
              )}
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
};
